import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePagination } from "./usePagination";
import { useAuthenticatedApi } from "./useAuthenticatedApi";

// Raw API response structure from backend
interface RawRestaurantResponse {
  restaurantid: string;
  hotelid: string;
  name: string;
  description: string;
  capacity: number;
  headerphoto?: string | { signedUrl: string; key: string; expiresAt: string };
  [key: string]: unknown; // Allow other fields
}

interface Restaurant {
  restaurantId: string;
  id: string; // Use restaurantid for id field
  restaurantid: string;
  hotelid: string;
  name: string;
  description: string;
  capacity: number;
  headerPhoto: string; // Updated to handle both string and object
}

interface ApiHotelRestaurantScheduleEntry {
  restaurantopscheduleid: string;
  restaurantid: string;
  intervalid: string;
  dayofweek: string;
  isactive: boolean;
  scheduletype: string;
  notes?: string;
  // Backend returns starttime and endtime at top level (from joined scheduleinterval table)
  starttime?: string;
  endtime?: string;
  intervaldescription?: string;
  // Some responses might also include nested interval object
  interval?: {
    intervalid: string;
    starttime: string;
    endtime: string;
    description: string;
  };
}

interface ScheduleInterval {
  id: string;
  startTime: string;
  endTime: string;
}

interface UseRestaurantsOptions {
  hotelId: string;
  initialPage?: number;
  initialLimit?: number;
}

export const useRestaurants = ({
  hotelId,
  initialPage = 1,
  initialLimit = 20,
}: UseRestaurantsOptions) => {
  const { callApi } = useAuthenticatedApi();
  const queryClient = useQueryClient();

  const [items, setItems] = useState<Restaurant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [cache, setCache] = useState<Record<string, Restaurant>>({});
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<
    string | null
  >(null);
  const [operatingSchedules, setOperatingSchedules] = useState<
    ApiHotelRestaurantScheduleEntry[] | null
  >(null);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);

  // New state for schedule intervals
  const [scheduleIntervals, setScheduleIntervals] = useState<
    ScheduleInterval[] | null
  >(null);
  const [isLoadingScheduleIntervals, setIsLoadingScheduleIntervals] =
    useState(false);

  const prevSelectedRestaurantIdRef = useRef<string | null>(null);

  const { pagination, setPage, setLimit, setTotal } = usePagination({
    initialPage,
    initialLimit,
  });

  // Cache management
  const updateCache = useCallback((item: Restaurant) => {
    setCache((prev) => ({
      ...prev,
      [item.restaurantId]: item,
    }));
  }, []);

  const removeFromCache = useCallback((id: string) => {
    setCache((prev) => {
      const newCache = { ...prev };
      delete newCache[id];
      return newCache;
    });
  }, []);

  // Helper to ensure time is in HH:MM:SS format
  const formatTimeToHHMMSS = (time: string): string => {
    if (/^([01]\d|2[0-3]):([0-5]\d)$/.test(time)) {
      // Matches HH:MM
      return `${time}:00`;
    }
    if (/^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/.test(time)) {
      // Matches HH:MM:SS
      return time;
    }
    console.warn(
      `Invalid time format provided to formatTimeToHHMMSS: ${time}. Defaulting to 00:00:00`,
    );
    return "00:00:00";
  };

  // Main restaurants query (server state) backed by React Query
  const {
    data: restaurantsResponse,
    isLoading: queryLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["restaurants", hotelId, pagination.page, pagination.limit],
    queryFn: async () => {
      const response = await callApi({
        method: "GET",
        url: `/management/hotel/restaurants`,
      });

      const restaurantData = response.data?.data || response.data || [];

      const transformedRestaurants: Restaurant[] = Array.isArray(restaurantData)
        ? restaurantData.map((restaurant: RawRestaurantResponse) => ({
            restaurantId: restaurant.restaurantid,
            id: restaurant.restaurantid,
            restaurantid: restaurant.restaurantid,
            hotelid: restaurant.hotelid,
            name: restaurant.name,
            description: restaurant.description,
            capacity: restaurant.capacity,
            headerPhoto:
              typeof restaurant.headerphoto === "object" &&
              restaurant.headerphoto?.signedUrl
                ? restaurant.headerphoto.signedUrl
                : (restaurant.headerphoto as string) || "",
          }))
        : [];

      return {
        items: transformedRestaurants,
        total:
          response.data?.meta?.totalCount ?? transformedRestaurants.length ?? 0,
      };
    },
    enabled: !!hotelId,
  });

  // Sync query results into existing local state + pagination
  useEffect(() => {
    setIsLoading(queryLoading);
  }, [queryLoading]);

  useEffect(() => {
    setError(queryError instanceof Error ? queryError : null);
  }, [queryError]);

  useEffect(() => {
    if (!restaurantsResponse) return;

    const { items: transformedRestaurants, total } = restaurantsResponse;
    setItems(transformedRestaurants);
    setTotal(total);
    transformedRestaurants.forEach((item: Restaurant) => updateCache(item));

    if (transformedRestaurants.length > 0 && !selectedRestaurantId) {
      setSelectedRestaurantId(transformedRestaurants[0].restaurantId);
    }
  }, [restaurantsResponse, setTotal, updateCache, selectedRestaurantId]);

  // Create item
  const createItem = useCallback(
    async (item: Omit<Restaurant, "restaurantId">) => {
      const response = await callApi({
        method: "POST",
        url: `/management/hotel/restaurants`,
        data: item,
      });

      const result = response.data.data;
      setItems((prev) => [...prev, result]);
      updateCache(result);
      queryClient.invalidateQueries({ queryKey: ["restaurants", hotelId] });
      return result;
    },
    [hotelId, callApi, updateCache, queryClient],
  );

  // Update item
  const updateItem = useCallback(
    async (id: string, updates: Partial<Restaurant>) => {
      // Transform camelCase field names back to snake_case for API
      const apiUpdates: any = { ...updates };
      if (updates.headerPhoto !== undefined) {
        apiUpdates.headerphoto = updates.headerPhoto;
        delete apiUpdates.headerPhoto;
      }

      const response = await callApi({
        method: "PUT",
        url: `/management/hotel/restaurants/${id}`,
        data: apiUpdates,
      });

      const result = response.data.data;
      setItems((prev) =>
        prev.map((item) => (item.restaurantId === id ? result : item)),
      );
      updateCache(result);
      queryClient.invalidateQueries({ queryKey: ["restaurants", hotelId] });
      return result;
    },
    [hotelId, callApi, updateCache, queryClient],
  );

  // Delete item
  const deleteItem = useCallback(
    async (id: string) => {
      await callApi({
        method: "DELETE",
        url: `/management/hotel/restaurants/${id}`,
      });

      setItems((prev) => prev.filter((item) => item.restaurantId !== id));
      removeFromCache(id);
      queryClient.invalidateQueries({ queryKey: ["restaurants", hotelId] });
    },
    [hotelId, callApi, removeFromCache, queryClient],
  );

  // Get selected restaurant
  const getSelectedRestaurant = useCallback(() => {
    if (!selectedRestaurantId) return null;
    return cache[selectedRestaurantId] || null;
  }, [selectedRestaurantId, cache]);

  // Fetch operating schedules for the selected restaurant
  const fetchOperatingSchedules = useCallback(async () => {
    if (!selectedRestaurantId || isLoadingSchedules) return;

    setIsLoadingSchedules(true);
    setError(null);
    try {
      const response = await callApi({
        method: "GET",
        url: `/management/hotel/restaurants/${selectedRestaurantId}/schedules`,
      });

      // Handle response structure properly - data might be nested
      const scheduleData = response.data?.data || response.data || [];

      // Ensure we always set an array
      setOperatingSchedules(Array.isArray(scheduleData) ? scheduleData : []);
    } catch (err) {
      console.error("Error fetching operating schedules:", err);
      setOperatingSchedules([]);
    } finally {
      setIsLoadingSchedules(false);
    }
  }, [selectedRestaurantId, hotelId, callApi, isLoadingSchedules]);

  useEffect(() => {
    if (
      selectedRestaurantId &&
      selectedRestaurantId !== prevSelectedRestaurantIdRef.current
    ) {
      fetchOperatingSchedules();
    }
    prevSelectedRestaurantIdRef.current = selectedRestaurantId;
  }, [selectedRestaurantId, fetchOperatingSchedules]);

  // Fetch schedule intervals for the current hotel
  const fetchScheduleIntervals = useCallback(async () => {
    if (!hotelId) return;

    setIsLoadingScheduleIntervals(true);
    try {
      const response = await callApi({
        method: "GET",
        url: `/management/schedule-intervals?limit=100`,
      });

      const intervalData = response.data?.data || response.data || [];
      setScheduleIntervals(Array.isArray(intervalData) ? intervalData : []);
    } catch (err) {
      console.error("Error fetching schedule intervals:", err);
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to fetch schedule intervals"),
      );
      setScheduleIntervals(null);
    } finally {
      setIsLoadingScheduleIntervals(false);
    }
  }, [hotelId, callApi]);

  // Effect to fetch intervals when hotelId is available/changes
  useEffect(() => {
    if (hotelId) {
      fetchScheduleIntervals();
    }
  }, [hotelId, fetchScheduleIntervals]);

  // Update restaurant description function
  const updateRestaurantDescription = useCallback(
    async (restaurantId: string, description: string) => {
      setError(null);
      try {
        const response = await callApi({
          method: "PUT",
          url: `/management/hotel/restaurants/${restaurantId}`,
          data: { description },
        });

        const result = response.data.data;
        setCache((prev) => ({
          ...prev,
          [restaurantId]: { ...prev[restaurantId], ...result },
        }));

        return result;
      } catch (err) {
        const newError =
          err instanceof Error
            ? err
            : new Error("Failed to update description");
        setError(newError);
        throw newError;
      }
    },
    [hotelId, callApi],
  );

  // Upsert (Create/Update) a restaurant's operating schedule for a specific day
  const upsertRestaurantOperatingSchedule = useCallback(
    async (
      currentRestaurantId: string,
      day: string,
      openTime: string,
      closeTime: string,
      isActive: boolean,
      scheduletype: string = "OperatingHours",
      notes: string | null = null,
      existingScheduleEntryForDay?: ApiHotelRestaurantScheduleEntry,
    ) => {
      if (!hotelId || !currentRestaurantId) {
        throw new Error("Hotel ID or Restaurant ID is missing.");
      }

      if (!scheduleIntervals && !isLoadingScheduleIntervals) {
        console.warn(
          "upsert: scheduleIntervals not loaded, attempting to fetch...",
        );
        await fetchScheduleIntervals();
        if (!scheduleIntervals) {
          throw new Error(
            "Schedule intervals are not available, cannot determine intervalid.",
          );
        }
      }

      // Ensure times are in HH:MM format (from HTML time input) before converting to HH:MM:SS
      // HTML time inputs return values in HH:MM format (24-hour, no seconds)
      const formattedOpenTime = formatTimeToHHMMSS(openTime);
      const formattedCloseTime = formatTimeToHHMMSS(closeTime);

      let intervalidToUse: string | undefined = undefined;

      // Step 1: Find or Create ScheduleInterval
      // NOTE: We always create a new interval if times don't match, rather than updating
      // the existing one, because intervals can be shared by multiple schedules (different
      // restaurants, menus, days, etc.). Updating an interval would affect all schedules
      // that use it, which is not desired.
      const existingMatchingInterval = scheduleIntervals?.find(
        (interval) =>
          interval.startTime === formattedOpenTime &&
          interval.endTime === formattedCloseTime,
      );

      if (existingMatchingInterval) {
        // Use existing interval with matching times
        intervalidToUse = existingMatchingInterval.id;
      } else {
        // Create new schedule interval with the new times
        // This ensures we don't accidentally modify times for other schedules
        // that might be sharing the same interval
        try {
          const intervalPayload = {
            startTime: formattedOpenTime,
            endTime: formattedCloseTime,
            description: `Operating Hours ${openTime}-${closeTime}`,
          };
          const response = await callApi({
            method: "POST",
            url: `/management/schedule-intervals`,
            data: intervalPayload,
          });

          const newInterval = response.data?.data || response.data;
          if (newInterval && newInterval.id) {
            intervalidToUse = newInterval.id;
            // Add to local cache
            setScheduleIntervals((prevIntervals) =>
              prevIntervals ? [...prevIntervals, newInterval] : [newInterval],
            );
          } else {
            console.error(
              "createScheduleInterval returned invalid data:",
              newInterval,
            );
            throw new Error(
              "Failed to create new schedule interval definition (invalid data).",
            );
          }
        } catch (err) {
          console.error("Error during createScheduleInterval call:", err);
          setError(
            err instanceof Error
              ? err
              : new Error("Failed to create time interval"),
          );
          throw err;
        }
      }

      if (!intervalidToUse) {
        console.error("Could not obtain an intervalid.");
        throw new Error(
          "Failed to find or create a suitable time interval ID.",
        );
      }

      // Step 2: Create or Update RestaurantOperatingSchedule entry
      try {
        if (
          existingScheduleEntryForDay &&
          existingScheduleEntryForDay.restaurantopscheduleid
        ) {
          const scheduleUpdatePayload = {
            intervalid: intervalidToUse,
            dayofweek: day,
            isactive: isActive,
            scheduletype: scheduletype,
            notes: notes,
          };
          await callApi({
            method: "PUT",
            url: `/management/hotel/restaurants/${currentRestaurantId}/schedules/${existingScheduleEntryForDay.restaurantopscheduleid}`,
            data: scheduleUpdatePayload,
          });
        } else {
          await callApi({
            method: "POST",
            url: `/management/hotel/restaurants/${currentRestaurantId}/schedules`,
            data: {
              intervalid: intervalidToUse,
              dayofweek: day,
              isactive: isActive,
              scheduletype: scheduletype,
              notes: notes,
            },
          });
        }

        // Refresh schedules and intervals after successful operation
        if (selectedRestaurantId === currentRestaurantId) {
          await fetchOperatingSchedules();
        }
        // Refresh intervals to ensure cache is up to date
        await fetchScheduleIntervals();
      } catch (err) {
        console.error(
          "Failed to create/update restaurant operating schedule entry:",
          err,
        );
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to save operating schedule for day"),
        );
        throw err;
      }
    },
    [
      hotelId,
      scheduleIntervals,
      isLoadingScheduleIntervals,
      fetchScheduleIntervals,
      fetchOperatingSchedules,
      selectedRestaurantId,
      callApi,
    ],
  );

  // Delete a restaurant's operating schedule for a specific day
  const deleteRestaurantOperatingSchedule = useCallback(
    async (currentRestaurantId: string, scheduleEntryId: string) => {
      if (!hotelId || !currentRestaurantId || !scheduleEntryId) {
        throw new Error("Missing IDs for deleting schedule entry.");
      }

      try {
        await callApi({
          method: "DELETE",
          url: `/management/hotel/restaurants/${currentRestaurantId}/schedules/${scheduleEntryId}`,
        });

        // Refresh schedules if it's the currently viewed restaurant
        if (selectedRestaurantId === currentRestaurantId) {
          await fetchOperatingSchedules();
        }
      } catch (err) {
        console.error(
          "Failed to delete restaurant operating schedule entry:",
          err,
        );
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to delete operating schedule entry"),
        );
        throw err;
      }
    },
    [hotelId, fetchOperatingSchedules, selectedRestaurantId, callApi],
  );

  return {
    items,
    isLoading,
    error,
    pagination,
    setPage,
    setLimit,
    selectedRestaurantId,
    setSelectedRestaurant: setSelectedRestaurantId,
    getSelectedRestaurant,
    createItem,
    updateItem,
    deleteItem,
    operatingSchedules,
    isLoadingSchedules,
    fetchOperatingSchedules,
    updateRestaurantDescription,
    scheduleIntervals,
    isLoadingScheduleIntervals,
    fetchScheduleIntervals,
    upsertRestaurantOperatingSchedule,
    deleteRestaurantOperatingSchedule,
  };
};
